import CartSideDrawer from "@/components/CartSideDrawer";
import type { CartType, CartItemType } from "@/types/cart";
import type { CompanyType } from "@/types/company";
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { useLoginModal } from "./LoginModalProvider";
import type { IngredientType } from "@/types/item";

interface CartContextType {
  addToCart: (company: CompanyType, cartItem: CartItemType) => void;
  removeFromCart: (cartItem: CartItemType) => void;
  changeCartItemQuantity: (cartItem: CartItemType, quantity: number) => void;
  changeCartItemIngredients: (
    cartItem: CartItemType,
    ingredients: IngredientType[]
  ) => void;
  openCart: () => void;
  closeCart: () => void;
  deleteActiveCart: () => void;
  itemsCount: number;
  cartItems: CartItemType[];
  totalPrice: number;
}

const cartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [carts, setCarts] = useState<CartType[]>(
    JSON.parse(localStorage.getItem("carts") || "[]")
  );
  const [activeCompany, setActiveCompany] = useState<CompanyType | undefined>(
    JSON.parse(localStorage.getItem("activeCompany") || "null")
  );
  const [isCartOpen, setIsCartOpen] = useState(false);

  const { isSignedIn } = useAuth();
  const { open: openLoginModal } = useLoginModal();

  const navigate = useNavigate();

  const openCart = () => {
    setIsCartOpen(true);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const addToCart = (company: CompanyType, cartItem: CartItemType) => {
    console.log("addToCart", cartItem);

    let cart = carts.find((cart) => cart.company.id === company.id);

    if (!cart) {
      cart = {
        company,
        cartItems: [cartItem],
      };
      setCarts([...carts, cart]);
    } else {
      const updatedCarts = carts.map((c) => {
        if (c.company.id === company.id) {
          const existingItem = c.cartItems.find(
            (item) =>
              item.item.id === cartItem.item.id &&
              JSON.stringify(item.ingredients) ===
                JSON.stringify(cartItem.ingredients)
          );
          if (existingItem) {
            return {
              ...c,
              cartItems: c.cartItems.map((item) =>
                item.item.id === cartItem.item.id &&
                JSON.stringify(item.ingredients) ===
                  JSON.stringify(cartItem.ingredients)
                  ? { ...item, quantity: item.quantity + 1 }
                  : item
              ),
            };
          }
          return {
            ...c,
            cartItems: [...c.cartItems, cartItem],
          };
        }
        return c;
      });
      setCarts(updatedCarts);
    }

    setActiveCompany(company);
  };

  useEffect(() => {
    if (
      carts.length > 0 &&
      !carts.find((cart) => cart.company.id === activeCompany?.id)
    ) {
      setActiveCompany(carts[0].company);
    }

    // save to local storage
    localStorage.setItem("carts", JSON.stringify(carts));
    localStorage.setItem("activeCompany", JSON.stringify(activeCompany));
  }, [carts]);

  const onSelectCart = (cart: CartType) => {
    setActiveCompany(cart.company);
  };

  const deleteActiveCart = () => {
    setCarts((prevCarts) => {
      return prevCarts.filter((cart) => cart.company.id !== activeCompany?.id);
    });
    setActiveCompany(carts[0].company ?? undefined);
  };

  const onIncreaseQuantity = (cartItem: CartItemType) => {
    changeCartItemQuantity(cartItem, cartItem.quantity + 1);
  };

  const onDecreaseQuantity = (cartItem: CartItemType) => {
    changeCartItemQuantity(cartItem, cartItem.quantity - 1);
  };

  const removeFromCart = (cartItem: CartItemType) => {
    console.log("removeFromCart", cartItem);
    changeCartItemQuantity(cartItem, 0);
    // setCarts((prevCarts) =>
    //   prevCarts
    //     .map((cart) => ({
    //       ...cart,
    //       cartItems: cart.cartItems.filter(
    //         (item) => item.item.id !== cartItem.item.id
    //       ),
    //     }))
    //     .filter((cart) => cart.cartItems.length > 0)
    // );
  };

  const onCompanyClick = (company: CompanyType) => {
    navigate(`/restaurants/${company.id}`);
  };

  const changeCartItemIngredients = (
    cartItem: CartItemType,
    ingredients: IngredientType[]
  ) => {
    console.log("changeCartItemIngredients", cartItem, ingredients);
    setCarts((prevCarts) =>
      prevCarts.map((cart) => {
        if (cart.company.id === activeCompany?.id) {
          const updatedItems = cart.cartItems.map((item) =>
            item.item.id === cartItem.item.id &&
            JSON.stringify(item.ingredients) === JSON.stringify(ingredients)
              ? { ...item, ingredients: ingredients }
              : item
          );

          return { ...cart, cartItems: updatedItems };
        }
        return cart;
      })
    );
  };

  const changeCartItemQuantity = (cartItem: CartItemType, qauntity: number) => {
    console.log("changeCartItemQuantity", cartItem, qauntity);
    setCarts(
      (prevCarts) =>
        prevCarts
          .map((cart) => {
            if (cart.company.id === activeCompany?.id) {
              const updatedItems = cart.cartItems
                .map((item) => {
                  console.log(
                    "item.item.id === cartItem.item.id: ",
                    item.item.id === cartItem.item.id
                  );
                  console.log(
                    "JSON.stringify(item.ingredients)",
                    JSON.stringify(item.ingredients)
                  );
                  console.log(
                    "JSON.stringify(cartItem.ingredients)",
                    JSON.stringify(cartItem.ingredients)
                  );
                  console.log(
                    "JSON.stringify(item.ingredients) === JSON.stringify(cartItem.ingredients): ",
                    JSON.stringify(item.ingredients) ===
                      JSON.stringify(cartItem.ingredients)
                  );
                  return item.item.id === cartItem.item.id &&
                    JSON.stringify(item.ingredients) ===
                      JSON.stringify(cartItem.ingredients)
                    ? { ...item, quantity: qauntity }
                    : item;
                })
                .filter((item) => item.quantity > 0); // remove items with quantity <= 0

              return { ...cart, cartItems: updatedItems };
            }
            return cart;
          })
          .filter((cart) => cart.cartItems.length > 0) // remove carts with no items
    );

    // console.log("changeCartItemQuantity", cartItem, qauntity);
    // console.log("carts", carts);
    // setCarts(
    //   (prevCarts) =>
    //     prevCarts
    //       .map((cart) => {
    //         if (cart.company.id === activeCompany?.id) {
    //           const updatedItems = cart.cartItems
    //             .map((item) =>
    //               item.item.id === cartItem.item.id &&
    //               JSON.stringify(item.ingredients) ===
    //                 JSON.stringify(cartItem.ingredients)
    //                 ? { ...item, quantity: qauntity }
    //                 : item
    //             )
    //             .filter((item) => item.quantity > 0); // remove items with quantity <= 0

    //           return { ...cart, cartItems: updatedItems };
    //         }
    //         return cart;
    //       })
    //       .filter((cart) => cart.cartItems.length > 0) // remove carts with no items
    // );
  };

  return (
    <cartContext.Provider
      value={{
        addToCart,
        removeFromCart,
        changeCartItemQuantity,
        changeCartItemIngredients,
        openCart,
        closeCart,
        deleteActiveCart,
        itemsCount:
          carts.find((cart) => cart.company.id === activeCompany?.id)?.cartItems
            .length ?? 0,
        cartItems:
          carts.find((cart) => cart.company.id === activeCompany?.id)
            ?.cartItems ?? [],
        totalPrice:
          carts
            .find((cart) => cart.company.id === activeCompany?.id)
            ?.cartItems.reduce(
              (acc, item) =>
                acc +
                item.quantity *
                  (item.item.price +
                    item.ingredients.reduce((acc, ing) => acc + ing.price, 0)),
              0
            ) ?? 0,
      }}
    >
      {children}
      <CartSideDrawer
        open={isCartOpen}
        onClose={() => closeCart()}
        activeCart={carts.find((cart) => cart.company.id === activeCompany?.id)}
        otherCarts={carts.filter(
          (cart) => cart.company.id !== activeCompany?.id
        )}
        onDeleteCart={() => {}}
        onIncreaseQuantity={onIncreaseQuantity}
        onDecreaseQuantity={onDecreaseQuantity}
        onSelectCart={onSelectCart}
        onCompanyClick={onCompanyClick}
        onItemClick={(item: CartItemType) => {
          console.log("itemClick in sidedrawer", item);
        }}
        onCheckout={() => {
          if (!isSignedIn) {
            openLoginModal(() => {
              closeCart();
              navigate("/checkout");
            });
            return;
          } else {
            navigate("/checkout");
            closeCart();
          }
        }}
      />
    </cartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(cartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
